// Centralized SEO configuration for all pages

// Product name → slug mapping
export const PRODUCT_SLUGS = {
  'Semaglutide': 'semaglutide',
  'Tirzepatide': 'tirzepatide',
  // 2026-02: Retatrutide removed from public catalog (hidden site-wide).
  'Cagrilintide': 'cagrilintide',
  'Survodutide': 'survodutide',
  'BPC-157': 'bpc-157',
  'TB-500 (Thymosin Beta-4)': 'tb-500',
  'TB-500': 'tb-500',
  'GHK-Cu': 'ghk-cu',
  'KPV (Lysine-Proline-Valine)': 'kpv',
  'KPV': 'kpv',
  'Semax': 'semax',
  'Selank': 'selank',
  'Pinealon': 'pinealon',
  'DSIP': 'dsip',
  'NAD+': 'nad-plus',
  'Epithalon': 'epithalon',
  'MOTS-c': 'mots-c',
  'Glutathione': 'glutathione',
  'SS-31': 'ss-31',
  'Ipamorelin': 'ipamorelin',
  'GHRP-6 Acetate': 'ghrp-6',
  'GHRP-6': 'ghrp-6',
  'CJC-1295 (Without DAC)': 'cjc-1295',
  'CJC-1295': 'cjc-1295',
  'CJC-1295 (With DAC)': 'cjc-1295-dac',
  'Sermorelin Acetate': 'sermorelin',
  'Sermorelin': 'sermorelin',
  'Tesamorelin': 'tesamorelin',
  'Thymosin Alpha-1': 'thymosin-alpha-1',
  'BPC-157 + TB-500': 'bpc-157-tb-500',
  'CJC-1295 + Ipamorelin': 'cjc-1295-ipamorelin',
  'Cagrilintide + Semaglutide': 'cagrilintide-semaglutide',
};

// Get slug from product name
export const getProductSlug = (name) => {
  if (!name) return '';
  return PRODUCT_SLUGS[name] || name.toLowerCase()
    .replace(/\(([^)]+)\)/g, '$1')
    .replace(/\+/g, '-')
    .replace(/[\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// Slugify a dosage string into a URL-safe suffix. '60 MG' -> '60mg',
// '5 MG + 5 MG' -> '5mg-5mg', '10 ml' -> '10ml'. Returns '' for empty/1-count.
export const slugifyDosage = (dosage) => {
  if (!dosage) return '';
  const s = String(dosage).trim().toLowerCase();
  if (s === '1' || s === '1ct' || s === '1pc') return '';
  return s
    .replace(/\s+/g, '')
    .replace(/\+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// Canonical descriptive product URL slug. Prefers the stored `product.slug`
// when present (admin can edit it / a migration can backfill it); falls back
// to deriving from name + dosage so older records without a stored slug
// still resolve to the correct URL.
//   getProductUrlSlug({slug:'tirzepatide-60mg'})                  -> 'tirzepatide-60mg'
//   getProductUrlSlug({name:'Semaglutide', dosage:'30 MG'})       -> 'semaglutide-30mg'
//   getProductUrlSlug({name:'BPC-157 + TB-500', dosage:'5 MG'})   -> 'bpc-157-tb-500-5mg'
export const getProductUrlSlug = (product) => {
  if (!product) return '';
  // Prefer the stored canonical slug. Trim to be defensive against legacy
  // records with whitespace. Empty string falls through to computed.
  const stored = (product.slug || '').trim();
  if (stored) return stored;
  const base = getProductSlug(product.name);
  if (!base) return '';
  const strength = slugifyDosage(product.dosage);
  return strength ? `${base}-${strength}` : base;
};

// ── Product mention JSON-LD builder (shared by React + SSR) ───────────────
// Returns a Schema.org/Product object with a complete `offers` block,
// or null if the product lacks the minimum fields Google requires
// (name, valid positive price, canonical URL). Never fakes reviews or
// aggregateRating. The SSR copy of this lives in scripts/prerender.js
// (kept identical by /app/backend/tests/test_product_mention_schema.js).
export const buildProductMentionSchema = (product, siteUrl = 'https://rcmbiosciences.com') => {
  if (!product) return null;
  const name = (product.name || '').trim();
  if (!name) return null;
  const rawPrice = product.price1;
  const priceNum = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice);
  if (!Number.isFinite(priceNum) || priceNum <= 0) return null;
  const slug = getProductUrlSlug(product);
  if (!slug) return null;
  // Google requires `image` on Product — no real image, no Product graph (never a placeholder).
  const rawImage = typeof product.image === 'string' ? product.image.trim() : '';
  if (!rawImage) return null;
  const image = /^https?:\/\//.test(rawImage) ? rawImage : `${siteUrl}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;
  const productUrl = `${siteUrl}/products/${slug}`;
  const fullName = product.dosage ? `${name} ${product.dosage}` : name;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${productUrl}#product`,
    name: fullName,
    brand: { '@type': 'Brand', name: 'RCM Biosciences' },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'USD',
      price: priceNum.toFixed(2),
      availability: product.inStock === false
        ? 'https://schema.org/BackOrder'
        : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };
  if (product.catNo) schema.sku = product.catNo;
  schema.image = image;
  // Description is `recommended` by the AEO validator. Fall back to a
  // generic catalog-listing line when the product has no per-record
  // description so we don't generate 1 warning per related-product
  // mention.
  schema.description = product.description
    || `${fullName} listed in the RCM Biosciences catalog for laboratory and analytical research. Research use only.`;
  return schema;
};

// Reverse lookup: slug → display name for meta
export const PRODUCT_DISPLAY_NAMES = {
  'semaglutide': 'Semaglutide',
  'tirzepatide': 'Tirzepatide',
  // 2026-02: Retatrutide removed from public catalog (hidden site-wide).
  'cagrilintide': 'Cagrilintide',
  'survodutide': 'Survodutide',
  'bpc-157': 'BPC-157',
  'tb-500': 'TB-500',
  'ghk-cu': 'GHK-Cu',
  'kpv': 'KPV',
  'semax': 'Semax',
  'selank': 'Selank',
  'pinealon': 'Pinealon',
  'dsip': 'DSIP',
  'nad-plus': 'NAD+',
  'epithalon': 'Epithalon',
  'mots-c': 'MOTS-c',
  'glutathione': 'Glutathione',
  'ss-31': 'SS-31',
  'ipamorelin': 'Ipamorelin',
  'ghrp-6': 'GHRP-6',
  'cjc-1295': 'CJC-1295',
  'cjc-1295-dac': 'CJC-1295 DAC',
  'sermorelin': 'Sermorelin',
  'tesamorelin': 'Tesamorelin',
  'thymosin-alpha-1': 'Thymosin Alpha-1',
  'bpc-157-tb-500': 'BPC-157 + TB-500',
  'cjc-1295-ipamorelin': 'CJC-1295 + Ipamorelin',
  'cagrilintide-semaglutide': 'Cagrilintide + Semaglutide',
};

// Category mapping: DB category → SEO category slug.
// Source of truth lives in ./categoryConfig.json so the build-time SSR
// (frontend/scripts/prerender.js) and the React app stay in lockstep. Add
// new categories THERE, not here.
import categoryConfig from './categoryConfig.json';

// CATEGORY_MAP keeps its historical name (DB → slug) — derived by inverting
// the canonical categorySlugToDb mapping so the JSON has only ONE direction
// of truth to maintain.
export const CATEGORY_MAP = Object.fromEntries(
  Object.entries(categoryConfig.categorySlugToDb).map(([slug, db]) => [db, slug])
);

// Product-level category overrides (for products in "immune_cellular" that span categories)
export const PRODUCT_CATEGORY_OVERRIDES = categoryConfig.productCategoryOverrides;

// Get SEO category for a product
export const getSeoCategory = (product) => {
  if (PRODUCT_CATEGORY_OVERRIDES[product.name]) return PRODUCT_CATEGORY_OVERRIDES[product.name];
  return CATEGORY_MAP[product.category] || product.category;
};

// Category SEO data
export const CATEGORY_SEO = {
  'weight-management': {
    title: 'Metabolic Research Peptides',
    description: 'Research-grade metabolic peptides including GLP-1, GIP, and dual/tri-incretin agonists, plus lipotropic blends supplied for in-vitro investigation of glucose homeostasis, lipid metabolism, and adipocyte biology. Independent third-party CoA per lot. Research use only.',
    h1: 'Metabolic Research Peptides',
    intro: 'Metabolic research compounds studied for their action on incretin receptors, adipocyte biology, lipid transport, and energy-metabolism pathways. Every SKU ships with an independent third-party Certificate of Analysis and is supplied strictly for in-vitro laboratory investigation.',
    dbCategories: ['metabolic'],
  },
  'tissue-recovery': {
    title: 'Regenerative Research Peptides',
    description: 'Research-grade regenerative peptides — BPC-157, TB-500, GHK-Cu, AHK-Cu, and related compounds — supplied for in-vitro investigation of angiogenesis, extracellular-matrix remodeling, and cellular repair signaling. Independent third-party CoA per lot. Research use only.',
    h1: 'Regenerative Research Peptides',
    intro: 'Regenerative research compounds studied for their role in angiogenic cascades, dermal-papilla biology, extracellular-matrix turnover, and cellular repair signaling. Every SKU ships with an independent third-party Certificate of Analysis and is supplied strictly for in-vitro laboratory investigation.',
    dbCategories: ['regenerative'],
  },
  'cognitive-neuro': {
    title: 'Cognitive & Neuro Research Peptides',
    description: 'Browse Cognitive & Neuro research peptides and bioscience compounds for laboratory and analytical research. Organized selection with clear research-use-only labeling.',
    h1: 'Cognitive & Neuro Research Peptides',
    intro: 'Explore cognitive and neuro research peptides organized for efficient catalog navigation and product inquiry.',
    dbCategories: ['cognitive'],
    extraProducts: ['DSIP'],
  },
  'longevity': {
    title: 'Longevity Research Compounds',
    description: 'Research-grade longevity compounds — NAD+, Epithalon, MOTS-c, SS-31, Glutathione — supplied for in-vitro investigation of mitochondrial function, oxidative stress, and cellular ageing pathways. Independent third-party CoA per lot. Research use only.',
    h1: 'Longevity Research Compounds',
    intro: 'Longevity-focused research compounds studied for their action on mitochondrial biology, redox balance, DNA-repair kinetics, and cellular senescence pathways. Every SKU ships with an independent third-party Certificate of Analysis and is supplied strictly for in-vitro laboratory investigation.',
    dbCategories: [],
    productNames: ['NAD+', 'Epithalon', 'MOTS-c', 'Glutathione', 'SS-31'],
  },
  'growth-hormone': {
    title: 'Growth Hormone Research Peptides',
    description: 'Research-grade growth-hormone secretagogues and related peptides — Ipamorelin, CJC-1295, Sermorelin, MK-677 analogs, FST-344 — supplied for in-vitro investigation of GHRH / ghrelin-receptor signaling and downstream IGF-1 pathways. Independent third-party CoA per lot. Research use only.',
    h1: 'Growth Hormone Research Peptides',
    intro: 'Growth-hormone research peptides studied for their action on GHRH and ghrelin receptors, pulsatile GH release kinetics, and downstream IGF-1 signaling. Every SKU ships with an independent third-party Certificate of Analysis and is supplied strictly for in-vitro laboratory investigation.',
    dbCategories: ['growth'],
  },
  'immune': {
    title: 'Immune & Cellular Research Peptides',
    description: 'Browse immune & cellular research peptides and bioscience compounds for laboratory and analytical research. Organized selection with clear research-use-only labeling.',
    h1: 'Immune & Cellular Research Peptides',
    intro: 'Browse immune & cellular research peptides presented with clear product navigation and research-use-only labeling.',
    dbCategories: ['immune_cellular'],
  },
  'blends': {
    title: 'Blends Research Peptides',
    description: 'Browse combination research peptides organized for laboratory and analytical research. Organized selection with clear research-use-only labeling.',
    h1: 'Blends Research Peptides',
    intro: 'Browse combination research peptides organized for laboratory and analytical research.',
    dbCategories: ['combination'],
  },
  // Same approved copy as prerender.js STATIC_SEO['lab-supplies'].
  'lab-supplies': {
    title: 'Research Lab Supplies',
    description: 'Laboratory research diluent (0.9% benzyl alcohol), peptide delivery pens, and other research lab supplies for laboratory and analytical research workflows.',
    h1: 'Research Lab Supplies',
    intro: 'Laboratory research diluent (0.9% benzyl alcohol), peptide delivery pens, and other research lab supplies for laboratory and analytical research workflows.',
    dbCategories: ['lab_supplies'],
  },
};

// Per-product supporting keyword phrases (imported from JSON so build-time
// prerender.js and runtime SEO stay in lockstep — same source of truth).
import productKeywords from './productKeywords.json';

// Product meta description template. Weaves in per-product supporting
// phrases when present so product pages carry the target keyword group
// without a bulky visible keyword block. The `dosage` argument is optional;
// when supplied, the strength appears in the description exactly once.
export const getProductMetaDescription = (name, dosage) => {
  const strength = dosage ? ` ${dosage}` : '';
  const base = `Explore ${name}${strength} for laboratory research, with product-specific specifications and available batch Certificate of Analysis documentation. Research use only.`;
  const extra = productKeywords[name] && productKeywords[name].phrases;
  return extra ? `${base} ${extra}` : base;
};

// Product intro template
export const getProductIntro = (name) =>
  `${name} is listed in the RCM Biosciences catalog for laboratory and analytical research. This page is provided for product identification, catalog navigation, and inquiry purposes only. Products are labeled for research use only.`;
